import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import CryptoJS from 'crypto-js';
import { useNavigate } from 'react-router-dom';
import { API_URL, customStyles, secret_key, storedData } from '../components/Constants';
import loadingGif from "../assets/loading.gif"
import './Student.css';

const Students = () => {
  const navigate = useNavigate();

  // --- Decrypt Token & User Data ---
  const encryptedToken = localStorage.getItem('token');
  let decryptedToken = '';
  if (encryptedToken) {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedToken, secret_key);
      decryptedToken = bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.error('Token decryption error:', error);
    }
  }
  const token = `Bearer ${decryptedToken}`;

  let loggedUserData = null;
  if (storedData) {
    const bytes = CryptoJS.AES.decrypt(storedData, secret_key);
    loggedUserData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  }

  // --- State ---
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);

  // --- Fetch Students Linked to Parent ---
  const fetchStudents = async () => {
    if (!loggedUserData) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/students/${loggedUserData.company_id}/${loggedUserData.parent_no}`,
            {
              headers: {
              Authorization: `CustomToken ${secret_key}`,
            },
            }
      );
       
      if (res.data.success) {
        setStudents(res.data.details || []);
      } else {
        Swal.fire("Info", res.data.message || "No linked students found", "info");
      }
    } catch (err) {
  console.error("Fetch students error:", err);

  const backendMessage =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    JSON.stringify(err?.response?.data);

  const status = err?.response?.status || "No status";
  const serverError = backendMessage || "Unknown server error";

  Swal.fire({
    title: "API Error",
    html: `
      <p><b>Status:</b> ${status}</p>
      <p><b>Message:</b> ${serverError}</p>
      <p><b>Token Sent:</b> ${token}</p>
    `,
    icon: "error",
    width: "600px",
  });

  if (status === 401) navigate(0);

} finally {
  setLoading(false);
}

  };

  // --- Fetch Student Fees Records (optional recent payments) ---
  const fetchRecentPayments = async () => {
    if (!loggedUserData) return;
    try {
      const res = await axios.get(
        `${API_URL}/StudentFeesRecords/${loggedUserData.company_id}/${loggedUserData.parent_no}`,
          {
                headers: {
                  Authorization: `CustomToken ${secret_key}`,
                },
              }
      );
      if (res.data.success) {
        const { details } = res.data;
        const payments = Object.entries(details || {}).flatMap(([admNo, data]) =>
          (data.recent_payments || []).map(p => ({
            date: p.date,
            document_no: p.document_no,
            admission_no: data.student_no,
            student_name: data.full_name,
            amount: parseFloat(p.paid || 0)
          }))
        );
        setRecentPayments(payments);
      }
    } catch (err) {
      console.error("Fetch recent payments error:", err);
    }
  };

  useEffect(() => {
    fetchStudents();
    fetchRecentPayments();
  }, []);

  // --- Format Currency ---
  const formatCurrency = (val) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
    }).format(val || 0);

  // --- Totals for Recent Payments ---
  const totalPayments = recentPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

  // --- Render ---
return (
<div className="dashboard-container container-fluid position-relative">

{/* Loading */}
{loading && (
<div className="loading-overlay d-flex align-items-center justify-content-center">
<img className="loading-img" src={loadingGif} alt="Loading..." />
</div>
)}

{/* Header */}
<header className="dashboard-header py-3 border-bottom mb-4">
<div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">

<h1 className="mb-2 mb-md-0 fw-bold">My Students</h1>

<nav aria-label="breadcrumb">
<ol className="breadcrumb mb-0">
<li className="breadcrumb-item"><a href="/">Home</a></li>
<li className="breadcrumb-item active">Students</li>
</ol>
</nav>

</div>
</header>

<div className="row">

{/* MAIN CONTENT */}
<div className="col-12 col-lg-8">

{/* ===== LINKED STUDENTS ===== */}

<div className="card shadow-sm rounded-4 mb-4">

<div className="card-header bg-gradient-primary text-white rounded-top-4">
<h5 className="mb-0">
<i className="fas fa-users me-2"></i>Linked Students
</h5>
</div>

<div className="card-body p-0">

{/* Desktop Table */}
<div className="table-responsive d-none d-md-block">

<table className="table table-striped mb-0">
<thead>
<tr>
<th>Admission No</th>
<th>Full Name</th>
<th>Class</th>
<th>Stream</th>
</tr>
</thead>

<tbody>
{students.length > 0 ? (

students.map((s,i)=>(
<tr key={i}>
<td>{s.admission_no}</td>
<td>{s.full_name}</td>
<td>{s.class || "-"}</td>
<td>{s.stream || "-"}</td>
</tr>
))

) : (

<tr>
<td colSpan="4" className="text-center text-muted py-3">
No linked students found
</td>
</tr>

)}
</tbody>
</table>

</div>

{/* Mobile Cards */}

<div className="d-md-none p-3">

{students.length > 0 ? (

students.map((s,i)=>(

<div className="mobile-card mb-3" key={i}>

<div className="mobile-card-header">

<strong>{s.full_name}</strong>
<span className="badge bg-primary">
{s.admission_no}
</span>

</div>

<div className="mobile-card-body">

<div className="mobile-row">
<span>Class</span>
<strong>{s.class || "-"}</strong>
</div>

<div className="mobile-row">
<span>Stream</span>
<strong>{s.stream || "-"}</strong>
</div>

</div>

</div>

))

) : (

<div className="text-center text-muted py-3">
No linked students found
</div>

)}

</div>

</div>
</div>


{/* ===== RECENT PAYMENTS ===== */}

<div className="card shadow-sm rounded-4 mb-4">

<div className="card-header bg-gradient-success text-white rounded-top-4">
<h5 className="mb-0">
<i className="fas fa-wallet me-2"></i>Recent Payments
</h5>
</div>

<div className="card-body p-0">

{/* Desktop Table */}

<div className="table-responsive d-none d-md-block">

<table className="table table-striped mb-0">

<thead>
<tr>
<th>Date</th>
<th>Document No</th>
<th>Admission No</th>
<th>Name</th>
<th>Amount</th>
</tr>
</thead>

<tbody>

{recentPayments.length > 0 ? (

<>

{recentPayments.map((p,i)=>(
<tr key={i}>
<td>{p.date}</td>
<td>{p.document_no}</td>
<td>{p.admission_no}</td>
<td>{p.student_name}</td>
<td>{formatCurrency(p.amount)}</td>
</tr>
))}

<tr className="fw-bold bg-light">
<td colSpan="4">Totals</td>
<td>{formatCurrency(totalPayments)}</td>
</tr>

</>

) : (

<tr>
<td colSpan="5" className="text-center text-muted py-3">
No recent payments
</td>
</tr>

)}

</tbody>
</table>

</div>

{/* Mobile Cards */}

<div className="d-md-none p-3">

{recentPayments.length > 0 ? (

<>

{recentPayments.map((p,i)=>(

<div className="mobile-card mb-3" key={i}>

<div className="mobile-card-header">

<strong>{p.student_name}</strong>

<span className="badge bg-success">
{formatCurrency(p.amount)}
</span>

</div>

<div className="mobile-card-body">

<div className="mobile-row">
<span>Date</span>
<strong>{p.date}</strong>
</div>

<div className="mobile-row">
<span>Document</span>
<strong>{p.document_no}</strong>
</div>

<div className="mobile-row">
<span>Admission</span>
<strong>{p.admission_no}</strong>
</div>

</div>

</div>

))}

<div className="mobile-total-card">

<strong>Total Payments</strong>

<strong className="text-success">
{formatCurrency(totalPayments)}
</strong>

</div>

</>

) : (

<div className="text-center text-muted py-3">
No recent payments
</div>

)}

</div>

</div>
</div>

</div>


{/* SIDE NOTES */}

<div className="col-12 col-lg-4">

<div className="card shadow-lg mb-4 border-0 rounded-4 notes-card">

<div className="card-header bg-gradient-secondary text-white rounded-top-4">

<h5 className="mb-0">
<i className="fas fa-info-circle me-2"></i>Notes
</h5>

</div>

<div className="card-body">

<p className="text-muted small mb-0">
Here you can view all students linked to your account and their latest fee payments.
</p>

</div>

</div>

</div>

</div>

</div>
);
};

export default Students;
